// Shared isometric-projection helpers used by the 3D SVG viewport ("fake 3D").
//
// Coordinate convention (world drawing space, meters):
//   x - column height, positive down (matches the shared Rendering.tsx convention)
//   y, z - the two cross-section axes (same as "xy" / "xz" side views)
//
// The projection below is a standard engineering isometric drawing (equal angles between the
// three axes on screen). It intentionally does not allow any camera rotation - "perfect"
// placement is fixed by design.
import { Point } from "@common/components/Rendering";

const COS30 = Math.cos(Math.PI / 6);
const SIN30 = Math.sin(Math.PI / 6);

export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export interface IsoScale {
  x: number; // scale applied to the column height (x) axis
  yz: number; // scale applied to the cross-section (y, z) axes
}

// Projects a 3D world point (x up, y/z cross-section) to a 2D isometric screen point.
// rightHand swaps the screen positions of the y/z axes without changing depth or vertical placement.
// screenV grows downward on screen (same convention as the rest of the viewport).
export const isoProject = (p: Point3D, scale: IsoScale, rightHand = false): Point => {
  const x = p.x * scale.x;
  const y = p.y * scale.yz;
  const z = p.z * scale.yz;
  const screenH = (rightHand ? z - y : y - z) * COS30;
  const screenV = (y + z) * SIN30 + x;
  return [screenH, screenV];
};
